import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { BlockDataForm, BlocksForm } from '@plone/volto/components/manage/Form';
import { SidebarPortal } from '@plone/volto/components';
import config from '@plone/volto/registry';
import EditBlockWrapper from '@plone/volto/components/manage/Blocks/Block/EditBlockWrapper';
import BlockChooserButton from '@plone/volto/components/manage/BlockChooser/BlockChooserButton';
import Schema from './schema';
import DestaqueView from './View';

// O formulário de blocos aninhado (isMainForm=false) só é editável com pelo
// menos um bloco: sem ele não há onde clicar para começar a escrever.
export const comTextoInicial = (data) => {
  if ((data?.blocks_layout?.items || []).length > 0) {
    return { blocks: data.blocks || {}, blocks_layout: data.blocks_layout };
  }
  const id = uuid();
  return {
    blocks: { [id]: { '@type': 'slate' } },
    blocks_layout: { items: [id] },
  };
};

// Botão do seletor de blocos do texto (substitui o ⊕ padrão do Volto).
const BotaoBloco = ({ onShowBlockChooser }) => (
  <button
    type="button"
    className="ag-destaque__add-bloco"
    title="Adicionar um bloco ao texto"
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onShowBlockChooser();
    }}
  >
    + Bloco
  </button>
);

/**
 * Edit do Destaque: o mesmo desenho do View, com o texto editável no lugar
 * (blocos do Volto, como na resposta da sanfona) e os campos na barra lateral.
 */
const DestaqueEdit = (props) => {
  const {
    block,
    data,
    onChangeBlock,
    selected,
    pathname,
    metadata,
    properties,
    manage,
  } = props;
  const [subBloco, setSubBloco] = useState(null);
  const conteudo = useMemo(
    () => comTextoInicial(data),
    [data?.blocks, data?.blocks_layout], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const schema = Schema();

  const gravarTexto = (novo) => onChangeBlock(block, { ...data, ...novo });

  // Acumulador por render: o volto-slate grava `blocks` e `blocks_layout` em
  // duas chamadas seguidas ao colar vários parágrafos (mesmo padrão da sanfona
  // e do Container do Volto); sem ele, a segunda apagaria a primeira.
  const blockState = {};

  const texto = (
    <>
      <BlocksForm
        metadata={metadata || properties}
        properties={conteudo}
        manage={manage}
        selectedBlock={selected ? subBloco : null}
        blocksConfig={config.blocks.blocksConfig}
        isMainForm={false}
        stopPropagation={subBloco}
        onSelectBlock={(id) => setSubBloco(id)}
        onChangeFormData={(novo) =>
          gravarTexto({
            blocks: novo.blocks,
            blocks_layout: novo.blocks_layout,
          })
        }
        onChangeField={(id, value) => {
          if (id === 'blocks' || id === 'blocks_layout') {
            blockState[id] = value;
            gravarTexto({ ...conteudo, ...blockState });
          }
        }}
        pathname={pathname}
      >
        {({ draginfo }, editBlock, blockProps) => (
          <EditBlockWrapper draginfo={draginfo} blockProps={blockProps}>
            {editBlock}
          </EditBlockWrapper>
        )}
      </BlocksForm>
      {selected && (
        <BlockChooserButton
          // `data` só decide se o botão aparece: um slate vazio sempre mostra.
          data={{ '@type': 'slate' }}
          block={conteudo.blocks_layout.items[0]}
          buttonComponent={BotaoBloco}
          blocksConfig={config.blocks.blocksConfig}
          properties={conteudo}
          onInsertBlock={(id, value) => {
            const nid = uuid();
            gravarTexto({
              blocks: { ...conteudo.blocks, [nid]: value },
              blocks_layout: {
                items: [...conteudo.blocks_layout.items, nid],
              },
            });
            setSubBloco(nid);
          }}
        />
      )}
    </>
  );

  const semImagem = (
    <div className="ag-destaque__midia ag-destaque__midia--vazia">
      Escolha a imagem na barra lateral
    </div>
  );

  return (
    <>
      <DestaqueView
        data={data}
        metadata={metadata}
        properties={properties}
        texto={texto}
        semImagem={semImagem}
      />
      <SidebarPortal selected={selected}>
        <BlockDataForm
          schema={schema}
          title={schema.title}
          onChangeField={(id, value) => {
            onChangeBlock(block, { ...data, [id]: value });
          }}
          formData={data}
          block={block}
        />
      </SidebarPortal>
    </>
  );
};

DestaqueEdit.propTypes = {
  block: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  onChangeBlock: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  pathname: PropTypes.string,
  metadata: PropTypes.object,
  properties: PropTypes.object,
  manage: PropTypes.bool,
};

export default DestaqueEdit;
